// qs-usePanelHandlers.ts
import { useDebounceFn } from '@vueuse/core'
import { emitDeleteModeChanged } from '@/composables/qs-useEventBus'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useToolStore } from '@/stores/qs-store-tools'
import type { SmoothingMode } from '@/types/qs-types-index'

type BrushType = 'round' | 'square' | 'dashed' | 'pointed' | 'bow-tie' | 'thick-to-thin' | 'thin-to-thick' | 'spray'

const VALID_BRUSH_TYPES: readonly BrushType[] = [
  'round',
  'square',
  'dashed',
  'pointed',
  'bow-tie',
  'thick-to-thin',
  'thin-to-thick',
  'spray'
] as const

const DEBOUNCE_DELAY = 50

export function usePanelHandlers() {
  const toolStore = useToolStore()
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()

  const dispatchLayerChangeEvent = () => {
    window.dispatchEvent(new CustomEvent('layer-changed'))
  }

  const isValidBrushType = (brushType: string): brushType is BrushType => {
    return VALID_BRUSH_TYPES.includes(brushType as BrushType)
  }

  const handleColorSelected = (color: string, opacity: number) => {
    try {
      toolStore.setColor(color)
      toolStore.setOpacity(opacity / 100)
    } catch (error) {
      console.error('Failed to set color:', error)
    }
  }

  const handleColorEdited = (_index: number, _color: string, _opacity: number) => {
  }

  const handlePanelHover = useDebounceFn((isHovered: boolean) => {
    try {
      canvasStore.setPanelHovered(isHovered)
    } catch (error) {
      console.error('Failed to set panel hover state:', error)
    }
  }, DEBOUNCE_DELAY)

  const handleBrushSizeChanged = async (size: number, _index: number) => {
    try {
      toolStore.setBrushSize(size)
      const { cursorManager } = await import('@/utils/qs-cursorManager')
      cursorManager.updateBrushSize(size)
    } catch (error) {
      console.error('Failed to set brush size:', error)
    }
  }

  const handleBrushItemSelected = (_index: number) => {
  }

  const handleBrushTypeChanged = (brushType: string, _index: number) => {
    try {
      if (isValidBrushType(brushType)) {
        toolStore.setBrushType(brushType)
      }
    } catch (error) {
      console.error('Failed to change brush type:', error)
    }
  }

  const handleBrushItemAdded = (_index: number, _size: number) => {
  }

  const handleBrushItemRemoved = (_index: number) => {
  }

  const handleBrushCountChanged = (_count: number) => {
  }

  const handleSmoothingModeChanged = (mode: SmoothingMode, _index: number) => {
    try {
      toolStore.setSmoothingMode(mode)
    } catch (error) {
      console.error('Failed to set smoothing mode:', error)
    }
  }

  const handleLayerSelected = (layerId: string) => {
    try {
      const success = layerStore.setCurrentLayer(layerId)
      if (success) {
        dispatchLayerChangeEvent()
      } else {
        console.warn('Failed to select layer - may be locked:', layerId)
      }
    } catch (error) {
      console.error('Failed to select layer:', error)
    }
  }

  const handleLayerAdded = (_layer: unknown) => {
    try {
      dispatchLayerChangeEvent()
    } catch (error) {
      console.error('Failed to add layer:', error)
    }
  }

  const handleLayerDeleted = (_layerId: string) => {
    try {
      dispatchLayerChangeEvent()
    } catch (error) {
      console.error('Failed to delete layer:', error)
    }
  }

  const handleLayerVisibilityChanged = (_layerId: string, _visible: boolean) => {
    try {
      dispatchLayerChangeEvent()
    } catch (error) {
      console.error('Failed to change layer visibility:', error)
    }
  }

  const handleLayerLockChanged = (_layerId: string, _locked: boolean) => {
    try {
      dispatchLayerChangeEvent()
    } catch (error) {
      console.error('Failed to change layer lock state:', error)
    }
  }

  const handleBackgroundColorChanged = (color: string) => {
    try {
      toolStore.setBackgroundColor(color)
      canvasStore.setBackgroundColor(color)
    } catch (error) {
      console.error('Failed to change background color:', error)
    }
  }

  const handleDeleteModeChanged = (enabled: boolean) => {
    try {
      emitDeleteModeChanged({ enabled })
    } catch (error) {
      console.error('Failed to change delete mode:', error)
    }
  }

  return {
    handleColorSelected,
    handleColorEdited,
    handlePanelHover,
    handleBrushSizeChanged,
    handleBrushItemSelected,
    handleBrushTypeChanged,
    handleBrushItemAdded,
    handleBrushItemRemoved,
    handleBrushCountChanged,
    handleSmoothingModeChanged,
    handleLayerSelected,
    handleLayerAdded,
    handleLayerDeleted,
    handleLayerVisibilityChanged,
    handleLayerLockChanged,
    handleBackgroundColorChanged,
    handleDeleteModeChanged
  }
}